import { Plus, Trash2 } from 'lucide-react'
import type { FieldDef } from '@/hooks/use-cheki-templates'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
}

const TYPE_LABELS: Record<FieldDef['type'], string> = {
  text: 'Teks',
  textarea: 'Area Teks',
  select: 'Pilihan',
  number: 'Angka',
}

interface FieldBuilderProps {
  fields: FieldDef[]
  onChange: (fields: FieldDef[]) => void
}

export default function FieldBuilder({ fields, onChange }: FieldBuilderProps) {
  function addField() {
    onChange([
      ...fields,
      { key: '', label: '', type: 'text', required: false, placeholder: '' },
    ])
  }

  function removeField(index: number) {
    onChange(fields.filter((_, i) => i !== index))
  }

  function updateField(index: number, patch: Partial<FieldDef>) {
    onChange(
      fields.map((f, i) => {
        if (i !== index) return f
        const updated = { ...f, ...patch }
        // Auto-slugify key when label changes, only if key was previously auto-derived
        if (patch.label !== undefined && f.key === slugify(f.label)) {
          updated.key = slugify(patch.label)
        }
        return updated
      })
    )
  }

  function updateOptions(index: number, raw: string) {
    const options = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    updateField(index, { options })
  }

  if (fields.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground italic">Belum ada field tambahan.</p>
        <button
          type="button"
          onClick={addField}
          className="flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <Plus className="h-4 w-4" />
          Tambah Field
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {fields.map((field, i) => (
        <div key={i} className="rounded-lg border border-border bg-background p-3 space-y-2">
          {/* Row 1: Label + Type + Required + Delete */}
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <label className="text-xs text-muted-foreground">Label *</label>
              <input
                value={field.label}
                onChange={(e) => updateField(i, { label: e.target.value })}
                placeholder='Contoh: "Ukuran Foto"'
                className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            <div className="w-32 space-y-1">
              <label className="text-xs text-muted-foreground">Tipe</label>
              <select
                value={field.type}
                onChange={(e) => updateField(i, { type: e.target.value as FieldDef['type'], options: [] })}
                className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
              >
                {(Object.keys(TYPE_LABELS) as FieldDef['type'][]).map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col items-center gap-1 pt-5">
              <label className="flex items-center gap-1 cursor-pointer" title="Wajib diisi">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => updateField(i, { required: e.target.checked })}
                  className="accent-primary"
                />
                <span className="text-xs text-muted-foreground">Wajib</span>
              </label>
            </div>
            <button
              type="button"
              onClick={() => removeField(i)}
              className="mt-5 p-1.5 text-destructive hover:bg-destructive/10 rounded"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Row 2: Options (only for select) */}
          {field.type === 'select' && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                Pilihan <span className="font-normal">(pisahkan dengan koma)</span>
              </label>
              <input
                value={(field.options ?? []).join(', ')}
                onChange={(e) => updateOptions(i, e.target.value)}
                placeholder='Contoh: "2R, 3R, 4R"'
                className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          )}

          {/* Row 3: Placeholder (not for select) */}
          {field.type !== 'select' && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Placeholder (opsional)</label>
              <input
                value={field.placeholder ?? ''}
                onChange={(e) => updateField(i, { placeholder: e.target.value })}
                placeholder='Teks petunjuk untuk fan'
                className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
              />
            </div>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addField}
        className="flex items-center gap-1.5 text-sm text-primary hover:underline"
      >
        <Plus className="h-4 w-4" />
        Tambah Field
      </button>
    </div>
  )
}
